import { useMemo, useState } from 'react';
import {
  CONTENT_HEIGHT,
  flattenPages,
  MAX_PAGES,
  overfullPages,
  pageHeights,
  partHeight,
  PART_GAP,
  splitPages,
  SUB_TIER_LABELS,
  SUB_TIERS,
  tidyWorksheetParts,
  type SubTier,
  type Worksheet,
  type WorksheetPart,
} from '@td/shared';
import { Modal } from './Modal';
import { WorksheetSheet } from './WorksheetSheet';

/**
 * Editing a worksheet without breaking it.
 *
 * A worksheet is a sheet of paper — 8½ by 11, and no amount of good intent
 * makes a twelfth inch appear. So the page is the constraint the editor is
 * built around rather than something it discovers at the printer: the space
 * left is shown while you type, a part that would not fit cannot be added,
 * and saving is refused while the page is over.
 *
 * The preview beside it is the same component that prints. There is no second
 * rendering to disagree with the first.
 */

const PART_LABELS: Record<WorksheetPart['kind'], string> = {
  text: 'A line of text',
  handwriting: 'Handwriting rules',
  lines: 'Writing lines',
  boxes: 'Boxes',
  problems: 'Numbered questions',
  wordbank: 'Word bank',
  drawbox: 'A box to draw in',
  numberline: 'Number line',
  tenframe: 'Ten frames',
  checklist: 'A checklist',
  table: 'A table',
  matching: 'Draw a line to match',
  circleit: 'Circle the answer',
  sort: 'Sorting columns',
  cutandpaste: 'Cut and glue',
  // Never offered as a thing to add — pages are added with their own button,
  // which is what a second sheet of paper actually feels like.
  pagebreak: 'A new page',
};

/** Everything she can put on a page, in the order the buttons appear. */
const ADDABLE = (Object.keys(PART_LABELS) as WorksheetPart['kind'][]).filter(
  (kind) => kind !== 'pagebreak',
);

/** A sensible starting point for each kind, so nothing is added empty. */
function blankPart(kind: WorksheetPart['kind']): WorksheetPart {
  switch (kind) {
    case 'text':
      return { kind, text: 'Write something here' };
    case 'handwriting':
      return { kind, lines: 2 };
    case 'lines':
      return { kind, lines: 3 };
    case 'boxes':
      return { kind, count: 1, columns: 1, label: '' };
    case 'problems':
      return { kind, problems: ['1 + 1 ='], answerLines: 0 };
    case 'wordbank':
      return { kind, words: ['one', 'two', 'three'] };
    case 'drawbox':
      return { kind, caption: 'Draw here', height: 150 };
    case 'numberline':
      return { kind, from: 0, to: 20, step: 5 };
    case 'tenframe':
      return { kind, frames: 2 };
    case 'checklist':
      return { kind, items: ['Capital letter', 'Full stop'] };
    case 'table':
      return { kind, headers: ['Word', 'How many'], rows: 4 };
    case 'matching':
      return { kind, left: ['one', 'two', 'three'], right: ['2', '3', '1'] };
    case 'circleit':
      return { kind, questions: [{ prompt: 'Circle the right one.', options: ['cat', 'dog'] }] };
    case 'sort':
      return { kind, columns: ['Yes', 'No'], height: 150 };
    case 'cutandpaste':
      return {
        kind,
        glueBoxes: 3,
        glueLabel: 'Glue them in order',
        items: ['first', 'next', 'last'],
      };
    case 'pagebreak':
      return { kind };
  }
}

export function WorksheetEditor({
  worksheet,
  onSave,
  onClose,
  saving,
  isNew = false,
  error = null,
}: {
  worksheet: Worksheet;
  onSave: (next: Worksheet) => void;
  onClose: () => void;
  saving: boolean;
  /** A blank sheet she is building rather than one she is changing. */
  isNew?: boolean;
  /**
   * Why the last save did not take.
   *
   * Shown rather than swallowed. A Save button that appears to work and
   * quietly does nothing is worse than one that says what went wrong — that
   * was the bug this prop exists because of.
   */
  error?: string | null;
}) {
  const title = isNew ? 'Build a worksheet' : 'Edit the worksheet';
  const [draft, setDraft] = useState<Worksheet>(worksheet);

  /*
   * The list is shown and edited in printed order, page by page, so what she
   * moves is what comes out of the printer. A cut-out strip is pinned to the
   * bottom of its own page by the renderer whatever happens here, and the
   * arrows respect that rather than appearing to work and then being overruled.
   */
  const pages = useMemo(() => splitPages(draft.parts), [draft.parts]);
  const heights = useMemo(() => pageHeights(draft), [draft]);
  const overfull = useMemo(() => overfullPages(draft), [draft]);
  const overflowing = overfull.length > 0;

  function setPages(next: WorksheetPart[][]) {
    setDraft({ ...draft, parts: flattenPages(next) });
  }

  /** A copy of the pages, safe to rearrange. */
  function copy(): WorksheetPart[][] {
    return pages.map((page) => [...page]);
  }

  function setPart(page: number, index: number, part: WorksheetPart) {
    const next = copy();
    next[page]![index] = part;
    setPages(next);
  }

  function remove(page: number, index: number) {
    const next = copy();
    next[page]!.splice(index, 1);
    setPages(next);
  }

  /**
   * Whether an arrow does anything.
   *
   * Within a page, a cut-out cannot swap with its neighbour — it lives at the
   * bottom and nothing goes under it. Off the end of a page is different: that
   * is a move to the next sheet of paper, which is a perfectly reasonable
   * thing to do with a cut-out, and it is how she fixes a page that is over.
   */
  function canMove(page: number, index: number, by: number): boolean {
    const here = pages[page]!;
    const to = index + by;

    if (to >= 0 && to < here.length) {
      return here[index]!.kind !== 'cutandpaste' && here[to]!.kind !== 'cutandpaste';
    }
    return by < 0 ? page > 0 : page < pages.length - 1;
  }

  function move(page: number, index: number, by: number) {
    if (!canMove(page, index, by)) return;

    const next = copy();
    const here = next[page]!;
    const to = index + by;

    if (to >= 0 && to < here.length) {
      [here[index], here[to]] = [here[to]!, here[index]!];
    } else if (by < 0) {
      next[page - 1]!.push(...here.splice(index, 1));
    } else {
      next[page + 1]!.unshift(...here.splice(index, 1));
    }

    setPages(next);
  }

  return (
    <Modal
      onClose={onClose}
      label={`Edit ${draft.title}`}
      className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-card bg-cream p-5 shadow-lifted"
    >
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <h2 className="text-lg">{title}</h2>
        <p className="text-xs text-stone">
          {pages.length === 1 ? 'One page' : `${pages.length} pages`} · 8½ × 11 in
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* ------------------------------------------------------- the form */}
        <div className="space-y-3">
          <label className="block">
            <span className="label">Title</span>
            <input
              className="field"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              maxLength={120}
            />
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="label">Subject</span>
              <input
                className="field"
                value={draft.subject}
                onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                maxLength={60}
              />
            </label>

            <label className="block">
              <span className="label">Time of year</span>
              <select
                className="field"
                value={draft.tier ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, tier: (e.target.value || null) as SubTier | null })
                }
              >
                <option value="">Any time</option>
                {SUB_TIERS.map((t) => (
                  <option key={t} value={t}>
                    {SUB_TIER_LABELS[t]}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="label">Instructions, read aloud before they start</span>
            <textarea
              className="field min-h-[4.5rem]"
              value={draft.instructions}
              onChange={(e) => setDraft({ ...draft, instructions: e.target.value })}
              maxLength={1000}
            />
          </label>

          <label className="block">
            <span className="label">Standards, comma separated</span>
            <input
              className="field"
              value={draft.standards.join(', ')}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  standards: e.target.value
                    .split(',')
                    .map((c) => c.trim())
                    .filter(Boolean),
                })
              }
              placeholder="1.RF.5, 1.W.1"
            />
          </label>

          {pages.map((page, p) => {
            const used = Math.round(heights[p] ?? 0);
            const room = CONTENT_HEIGHT - used;
            const over = room < 0;
            const percent = Math.min(100, Math.round((used / CONTENT_HEIGHT) * 100));

            return (
              <div key={p} className="rounded-card border border-sand bg-linen/40 p-2.5">
                <div className="mb-2">
                  <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
                    <span className="label mb-0">
                      {pages.length === 1 ? 'What is on the page' : `Page ${p + 1}`}
                    </span>

                    <span className="flex items-center gap-2 text-xs">
                      <span className={over ? 'text-rust' : 'text-stone'}>
                        {over
                          ? `${-room}px past the bottom of the page`
                          : `${room}px of the page left`}
                      </span>

                      {/*
                        A page can only be taken away while it is empty. A
                        button that silently binned an afternoon of typing
                        would be a worse bug than the one it saved her from.
                      */}
                      {pages.length > 1 && page.length === 0 && (
                        <button
                          className="btn-soft-danger px-1.5 py-0.5"
                          onClick={() => setPages(pages.filter((_, n) => n !== p))}
                        >
                          Remove this page
                        </button>
                      )}
                    </span>
                  </div>

                  {/*
                    The page, as a bar. A worksheet is a sheet of paper, and
                    the only question that matters while editing one is whether
                    it still fits.
                  */}
                  <div className="h-2 overflow-hidden rounded-full bg-white">
                    <div
                      className={`h-full transition-all ${over ? 'bg-rust' : 'bg-olive'}`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>

                <ul className="space-y-2">
                  {page.map((part, i) => (
                    <li key={i} className="rounded-lg border border-sand bg-white p-2">
                      <div className="mb-1.5 flex items-center gap-1.5">
                        <span className="min-w-0 flex-1 truncate text-xs text-walnut">
                          {PART_LABELS[part.kind]}
                          <span className="ml-1.5 text-stone">
                            {Math.round(partHeight(part))}px
                          </span>
                          {part.kind === 'cutandpaste' && (
                            <span
                              className="ml-1.5 text-stone"
                              title="Everything below the cut line leaves the page with the cut-outs, so this always prints last."
                            >
                              · always last
                            </span>
                          )}
                        </span>

                        <button
                          className="btn-soft px-1.5"
                          onClick={() => move(p, i, -1)}
                          disabled={!canMove(p, i, -1)}
                          aria-label="Move up"
                          title={i === 0 && p > 0 ? 'Move to the end of the page before' : 'Move up'}
                        >
                          ↑
                        </button>
                        <button
                          className="btn-soft px-1.5"
                          onClick={() => move(p, i, 1)}
                          disabled={!canMove(p, i, 1)}
                          aria-label="Move down"
                          title={
                            i === page.length - 1 && p < pages.length - 1
                              ? 'Move to the top of the next page'
                              : 'Move down'
                          }
                        >
                          ↓
                        </button>
                        <button
                          className="btn-soft-danger px-1.5"
                          onClick={() => remove(p, i)}
                          aria-label="Remove"
                        >
                          ✕
                        </button>
                      </div>

                      <PartFields part={part} onChange={(next) => setPart(p, i, next)} />
                    </li>
                  ))}
                </ul>

                <div className="mt-2 flex flex-wrap gap-1.5">
                  {ADDABLE.map((kind) => {
                    const would = partHeight(blankPart(kind)) + PART_GAP;
                    const fits = would <= room;

                    return (
                      <button
                        key={kind}
                        className="btn-soft"
                        disabled={!fits}
                        title={
                          fits
                            ? `Add ${PART_LABELS[kind].toLowerCase()}`
                            : `There is not enough room left on page ${p + 1} for this. Add a page, or move something down to the next one.`
                        }
                        onClick={() => {
                          const next = copy();
                          next[p]!.push(blankPart(kind));
                          setPages(next);
                        }}
                      >
                        + {PART_LABELS[kind]}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/*
            A second sheet, when one is honestly not enough — and it is a
            decision she makes, not something that happens to her at the
            photocopier. Each page is measured on its own, so page two of two
            cannot quietly become page three.
          */}
          {pages.length < MAX_PAGES && (
            <button
              className="btn-secondary w-full"
              onClick={() => setPages([...pages, []])}
            >
              + Add another page
            </button>
          )}
        </div>

        {/* ---------------------------------------------------- the preview */}
        <div className="lg:sticky lg:top-0 lg:self-start">
          <p className="label">{pages.length === 1 ? 'The page' : 'The pages'}</p>
          <div
            className={[
              'overflow-hidden rounded-card border-2',
              overflowing ? 'border-rust' : 'border-sand',
            ].join(' ')}
          >
            {/*
              Scaled down to fit beside the form, but it is the component that
              prints — so what is shown here is what comes out of the machine.
            */}
            <div style={{ zoom: 0.62 }}>
              <WorksheetSheet sheet={draft} />
            </div>
          </div>
        </div>
      </div>

      {overflowing && (
        <p className="mt-3 text-sm text-rust">
          {pages.length === 1
            ? 'This is longer than one sheet of paper. Take something off, make a box shorter, or add a second page, and it will save.'
            : `Page ${overfull.map((o) => o.page).join(' and ')} ${
                overfull.length > 1 ? 'run' : 'runs'
              } past the bottom of the paper. Move something down to the next page, or take it off, and it will save.`}
        </p>
      )}

      {error && <p className="mt-3 text-sm text-rust">It did not save: {error}</p>}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          className="btn-primary"
          disabled={overflowing || saving || !draft.title.trim()}
          onClick={() => onSave({ ...draft, parts: tidyWorksheetParts(draft.parts) })}
        >
          {saving ? 'Saving…' : isNew ? 'Make the worksheet' : 'Save the worksheet'}
        </button>
        <button className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}

/** The fields for one part, whichever kind it is. */
function PartFields({
  part,
  onChange,
}: {
  part: WorksheetPart;
  onChange: (next: WorksheetPart) => void;
}) {
  const number = (label: string, value: number, set: (n: number) => void, min = 1, max = 40) => (
    <label className="flex items-center gap-1.5 text-xs text-walnut">
      {label}
      <input
        type="number"
        className="field w-16 py-1"
        value={value}
        min={min}
        max={max}
        onChange={(e) => set(Math.max(min, Math.min(max, Number(e.target.value) || min)))}
      />
    </label>
  );

  /**
   * A list, one per line.
   *
   * Note what is *not* here: a filter. It used to drop empty lines on every
   * keystroke, which meant pressing Enter to start a fourth item deleted the
   * blank line it had just made — the cursor sprang back and the list could
   * never grow past what it already had. Split and join round-trip exactly
   * when nothing is thrown away, so the textarea now behaves like a textarea.
   * The empty lines are tidied up once, on save.
   */
  const list = (label: string, values: string[], set: (v: string[]) => void) => (
    <label className="block text-xs text-walnut">
      {label}
      <textarea
        className="field min-h-[3.5rem] text-sm"
        value={values.join('\n')}
        onChange={(e) => set(e.target.value.split('\n'))}
      />
    </label>
  );

  switch (part.kind) {
    case 'text':
      return (
        <input
          className="field text-sm"
          value={part.text}
          onChange={(e) => onChange({ ...part, text: e.target.value })}
        />
      );

    case 'handwriting':
      return (
        <div className="space-y-1.5">
          {number('Lines', part.lines, (lines) => onChange({ ...part, lines }), 1, 12)}
          {list('To trace — one line each, blank for none', part.trace ?? [], (trace) =>
            onChange({ ...part, trace: trace.length > 0 ? trace : undefined }),
          )}
        </div>
      );

    case 'lines':
      return number('Lines', part.lines, (lines) => onChange({ ...part, lines }), 1, 20);

    case 'boxes':
      return (
        <div className="flex flex-wrap gap-3">
          {number('How many', part.count, (count) => onChange({ ...part, count }), 1, 60)}
          {number('Per row', part.columns, (columns) => onChange({ ...part, columns }), 1, 12)}
          <input
            className="field w-40 py-1 text-sm"
            placeholder="Label"
            value={part.label ?? ''}
            onChange={(e) => onChange({ ...part, label: e.target.value })}
          />
        </div>
      );

    case 'problems':
      return (
        <div className="space-y-1.5">
          {list('One question per line', part.problems, (problems) =>
            onChange({ ...part, problems }),
          )}
          {number(
            'Lines to answer on (0 for a box)',
            part.answerLines,
            (answerLines) => onChange({ ...part, answerLines }),
            0,
            6,
          )}
        </div>
      );

    case 'wordbank':
      return list('One word per line', part.words, (words) => onChange({ ...part, words }));

    case 'drawbox':
      return (
        <div className="flex flex-wrap gap-3">
          <input
            className="field w-48 py-1 text-sm"
            placeholder="Caption"
            value={part.caption}
            onChange={(e) => onChange({ ...part, caption: e.target.value })}
          />
          {number('Height', part.height, (height) => onChange({ ...part, height }), 60, 400)}
        </div>
      );

    case 'numberline':
      return (
        <div className="flex flex-wrap gap-3">
          {number('From', part.from, (from) => onChange({ ...part, from }), 0, 200)}
          {number('To', part.to, (to) => onChange({ ...part, to }), 1, 200)}
          {number('Step', part.step, (step) => onChange({ ...part, step }), 1, 50)}
        </div>
      );

    case 'tenframe':
      return number('Frames', part.frames, (frames) => onChange({ ...part, frames }), 1, 8);

    case 'checklist':
      return list('One item per line', part.items, (items) => onChange({ ...part, items }));

    case 'table':
      return (
        <div className="space-y-1.5">
          {list('One column heading per line', part.headers, (headers) =>
            onChange({ ...part, headers }),
          )}
          {number('Rows', part.rows, (rows) => onChange({ ...part, rows }), 1, 20)}
        </div>
      );

    case 'matching':
      return (
        <div className="grid gap-1.5 sm:grid-cols-2">
          {list('Left column, one per line', part.left, (left) => onChange({ ...part, left }))}
          {list('Right column — shuffle these', part.right, (right) =>
            onChange({ ...part, right }),
          )}
        </div>
      );

    case 'circleit':
      return (
        <div className="space-y-1.5">
          {part.questions.map((q, i) => (
            <div key={i} className="rounded border border-sand p-1.5">
              <input
                className="field mb-1 text-sm"
                value={q.prompt}
                placeholder="What to circle"
                onChange={(e) =>
                  onChange({
                    ...part,
                    questions: part.questions.map((old, n) =>
                      n === i ? { ...old, prompt: e.target.value } : old,
                    ),
                  })
                }
              />
              <input
                className="field text-sm"
                value={q.options.join(', ')}
                placeholder="Choices, comma separated"
                onChange={(e) =>
                  onChange({
                    ...part,
                    questions: part.questions.map((old, n) =>
                      n === i
                        ? {
                            ...old,
                            options: e.target.value
                              .split(',')
                              .map((o) => o.trim())
                              .filter(Boolean),
                          }
                        : old,
                    ),
                  })
                }
              />
            </div>
          ))}
          <button
            className="btn-soft"
            onClick={() =>
              onChange({ ...part, questions: [...part.questions, { prompt: '', options: [] }] })
            }
          >
            + Another one to circle
          </button>
        </div>
      );

    case 'sort':
      return (
        <div className="space-y-1.5">
          {list('One column heading per line', part.columns, (columns) =>
            onChange({ ...part, columns }),
          )}
          {number('How tall', part.height, (height) => onChange({ ...part, height }), 80, 340)}
        </div>
      );

    case 'cutandpaste':
      return (
        <div className="space-y-1.5">
          <input
            className="field text-sm"
            placeholder="What to do with the glue boxes"
            value={part.glueLabel ?? ''}
            onChange={(e) => onChange({ ...part, glueLabel: e.target.value })}
          />
          {number(
            'Glue boxes',
            part.glueBoxes,
            (glueBoxes) => onChange({ ...part, glueBoxes }),
            1,
            9,
          )}
          {list('Things to cut out, one per line', part.items, (items) =>
            onChange({ ...part, items }),
          )}
        </div>
      );
  }
}
